import {Button} from "@/components/ui/Button";
import {Icon} from "@/components/ui/icon";
import {Info, Loader2, Rocket} from "lucide-react";
import {Alert} from "@/components/ui/Alert";
import {useSave} from "@/contexts/SaveContext";
import {cn} from "@/lib/utils";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {Dialog} from "@/components/ui/dialog";

export const DeployModal = ({
  onPublish,
  showModal,
  setShowModal,
  isPublishing,
}: {
  onPublish: () => void;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  isPublishing: boolean;
}) => {
  const {flowIssues} = useSave();
  const hasIssues = flowIssues.length > 0;

  const handlePublish = async () => {
    if (onPublish) {
      await onPublish();
    }
  };

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent
        className={cn(
          "w-110 min-w-110 block overflow-hidden",
          isPublishing && "[&>button[data-dialog-close]]:hidden",
        )}
        onInteractOutside={e => isPublishing && e.preventDefault()}
        onEscapeKeyDown={e => isPublishing && e.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Publish to Live</DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">
          Are you sure you want to publish the flow to Live? This will replace
          the existing Live version and cannot be restored.
        </DialogDescription>
        <div className="bg-white p-6 rounded-lg w-110 flex flex-col items-center gap-5">
          <Icon
            className={cn(
              "bg-opacity-10 border-opacity-30 rounded-lg",
              hasIssues
                ? "bg-yellow border-yellow"
                : "bg-primary border-primary",
            )}
            size="lg"
          >
            {isPublishing ? (
              <Loader2
                className={cn(
                  "animate-spin text-primary-accent",
                  hasIssues ? "text-yellow" : "text-primary-accent",
                )}
                size={28}
              />
            ) : (
              <Rocket
                size={28}
                className={cn(
                  hasIssues ? "text-yellow" : "text-primary-accent",
                  isPublishing && "animate-spin",
                )}
              />
            )}
          </Icon>
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="text-lg font-bold">
              {isPublishing ? "Publishing..." : "Publish to Live"}
            </h2>
            <p className="text-sm text-label">
              Are you sure you want to publish the flow to Live? This will
              replace the existing Live version and cannot be restored.
            </p>
          </div>
          {hasIssues && (
            <Alert variant="warning" className="flex items-center">
              <Info className="h-4 w-4" />
              <b className="mr-1">You have unresolved issues.</b> Publish
              anyway?
            </Alert>
          )}
          <div className="flex justify-between w-full gap-3">
            <Button
              onClick={() => setShowModal(false)}
              variant="outline"
              className="w-1/2"
              disabled={isPublishing}
            >
              No, Return to Flow
            </Button>
            <Button
              onClick={handlePublish}
              className="w-1/2"
              variant={hasIssues ? "outline-destructive" : "default"}
              disabled={isPublishing}
            >
              {isPublishing ? "Publishing..." : "Yes, Publish"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};